// ============================================================
// Versioned form phrasing — DB-backed lookup.
//
// A plan pins (form_template_id, form_version) at authoring time.
// This hook loads that immutable published version plus the template's
// current state, so the editor can render the wording exactly as the
// educator saw it and point out where the district has since changed it.
// Plans on the built-in Danielson catalogue (no template / no published
// versions) fall back to the static FORM_VERSIONS in lib/versions.
// ============================================================
import { useQuery } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import type { FormTemplate, FormTemplateVersion } from '../lib/types'

export interface UseFormVersion {
  /** The immutable version the plan was authored under (if published). */
  pinned: FormTemplateVersion | null
  /** The template's present state (name, current_version, working structure). */
  template: FormTemplate | null
}

export function useFormVersion(templateId: string | null | undefined, version: number): UseFormVersion {
  const { data: pinned = null } = useQuery<FormTemplateVersion | null>({
    queryKey: ['form-version', templateId, version],
    queryFn: async () => {
      if (!templateId) return null
      const { data } = await supabase
        .from('form_template_versions')
        .select('*')
        .eq('template_id', templateId)
        .eq('version', version)
        .maybeSingle()
      return (data as unknown as FormTemplateVersion) ?? null
    },
    enabled: !!templateId && version > 0,
    staleTime: Infinity, // published versions are immutable
  })

  const { data: template = null } = useQuery<FormTemplate | null>({
    queryKey: ['form-template', templateId],
    queryFn: async () => {
      if (!templateId) return null
      const { data } = await supabase
        .from('form_templates')
        .select('*')
        .eq('id', templateId)
        .maybeSingle()
      return (data as unknown as FormTemplate) ?? null
    },
    enabled: !!templateId,
    staleTime: 60_000,
  })

  return { pinned, template }
}
